import axios, { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios';
import * as rax from 'retry-axios';
import EventEmitter from 'eventemitter3';
import TokenExpiredError from './TokenExpiredError';
import { LeadFormAllowedRouteKeys } from '~/constants/lead-forms.consts';
import { trackError } from '~/services/logger/error.utils';
import {
    HB_BASE_ERROR_CODES,
    IApiService
} from '~/services/interfaces/IApiService';
import { BaseGlobalContextValue } from '~/providers/base-global-context/base-global.context';
import { isReactFirst } from '~/utils/environment.utils';
import { REACT_STORAGE_AUTH_KEY } from '~/common/storage.constants';

const DEFAULT_HEADERS = {
    'Content-Type': 'application/json'
};

const REQUEST_TIMEOUT = 30000; // Our server timeout is 20s so it's with 10 seconds buffer.
export const DEFAULT_RETRY_CONFIG: rax.RetryConfig = {
    retry: 3,
    noResponseRetries: 3, // Retry when there was no response (ENOTFOUND, ETIMEDOUT, etc)
    backoffType: 'static', // Retry every
    retryDelay: 100,
    checkRetryAfter: false,
    httpMethodsToRetry: ['GET'] // We don't want to retry actions that alter data automatically on network error without data check.
};
export interface IAPIParams extends AxiosRequestConfig {
    networkLabel?: string;
    isJsonp?: boolean;
    leadFormRouteKey?: LeadFormAllowedRouteKeys;
}

export function getFullStoryUrl() {
    if (window && window.FS && typeof window.FS.getSession === 'function') {
        return window.FS('getSession', { format: 'url' });
    }

    return undefined;
}

export function sendFullStoryEvent(eventName, eventProperties) {
    if (window && window.FS && typeof window.FS.trackEvent === 'function') {
        window.FS('trackEvent', {
            name: eventName,
            properties: eventProperties
        });
    }
}

export class ApiEventEmitter extends EventEmitter<{
    requestSuccess: (data: {
        request: AxiosRequestConfig;
        response: AxiosResponse;
    }) => void;
}> {}

rax.attach();
export abstract class BaseApiService implements IApiService {
    // TODO: Brake it to specific services
    globalContext: BaseGlobalContextValue;
    apiEventEmitter = new ApiEventEmitter();
    private responseMiddlewares: Array<
        (response: AxiosResponse) => Promise<AxiosResponse> | AxiosResponse
    > = [];
    private requestMiddlewares: Array<
        (
            config: AxiosRequestConfig
        ) => Promise<AxiosRequestConfig> | AxiosRequestConfig
    > = [];

    constructor(globalContext: BaseGlobalContextValue) {
        this.globalContext = globalContext;
        this.setupInterceptors();
    }

    async apiRequest<T>({
        method = 'get',
        url,
        params = {},
        data = {},
        headers,
        networkLabel = 'general',
        isJsonp = false,
        ...axiosOtherOptions
    }: IAPIParams): Promise<T> {
        const { networkStore } = this.globalContext;

        networkStore.startNetwork(networkLabel);

        const enrichedData = {
            ...data,
            full_story_session_url: getFullStoryUrl()
        };

        try {
            const manipulatedConfig = this.requestInterceptor({
                method,
                url,
                params,
                data: enrichedData,
                headers: { ...DEFAULT_HEADERS, ...headers }
            });

            const request = {
                baseURL: this.getBaseUrl(),
                timeout: REQUEST_TIMEOUT,
                raxConfig: DEFAULT_RETRY_CONFIG,
                ...axiosOtherOptions,
                ...manipulatedConfig
            };
            const response = await axios(request);

            let resultData = response.data;
            if (isJsonp) {
                resultData = this.extractJsonFromJsonP(resultData);
            }

            this.apiEventEmitter.emit('requestSuccess', {
                request,
                response
            });

            return resultData;
        } catch (err) {
            this.globalContext.fullstoryService.error(err);

            return await this.handleRoutineError(err);
        } finally {
            networkStore.endNetwork(networkLabel);
        }
    }

    protected handleRoutineError(err: AxiosError<any>): Promise<any> {
        if (!err?.response?.data) {
            // This means the error is not from our API, it's probably a network error
            // in this case err.response is an XMLHttpRequest object and err.response.data is empty
            // the error message is in err.message
            return Promise.reject(err);
        }

        const { error_type } = err.response.data;

        if (!error_type) {
            return Promise.reject(err.response);
        }

        const {
            configStore: {
                config: { hb_base_error_codes }
            }
        } = this.globalContext;

        if (!hb_base_error_codes) {
            trackError({
                error: err,
                message: 'hb_base_error_codes is not defined',
                context: {
                    // if it's an Axios error it would have the config
                    url: err.response.config?.url,
                    method: err.response.config?.method
                }
            });
        }

        const errorCodes = hb_base_error_codes || HB_BASE_ERROR_CODES;

        if (error_type === errorCodes.unauthorized_error) {
            trackError({
                error: new Error(`${errorCodes.authorized_error}`),
                message: 'Token expired'
            });

            this.onUnauthorizedError();

            return Promise.reject<TokenExpiredError>(new TokenExpiredError());
        }
        if (error_type === errorCodes.wrong_api_version_error) {
            // After the server deploys, the api_version is changed
            // TODO: Maybe display a toaster
            trackError({
                error: new Error(`${errorCodes.wrong_api_version_error}`),
                message: 'Wrong server api version, reloading...'
            });

            window.location.reload();
        }

        return Promise.reject(err.response);
    }

    protected extractJsonFromJsonP(jsonPText: string) {
        const startJsonIndex = jsonPText.indexOf('(') + 1;
        const endJsonIndex = jsonPText.lastIndexOf(')');

        if (
            startJsonIndex < 0 ||
            endJsonIndex < 0 ||
            startJsonIndex >= endJsonIndex
        ) {
            return jsonPText;
        }

        try {
            return JSON.parse(jsonPText.slice(startJsonIndex, endJsonIndex));
        } catch (error) {
            trackError({
                error,
                message: 'Failed to parse JSONP response',
                context: { jsonPText }
            });

            return jsonPText;
        }
    }

    protected onUnauthorizedError() {
        if (isReactFirst()) {
            // TODO: would prefer to call sessionCtrl.localLogout here but it's not available
            // do we want it on the global context?
            //this.globalContext.sessionCtrl.localLogout();
            localStorage.removeItem(REACT_STORAGE_AUTH_KEY);
        }

        // if under angular, reload and let angular take care of it (in users manager)
        window.location.reload();
    }

    protected requestInterceptor(
        config: AxiosRequestConfig
    ): AxiosRequestConfig {
        const headers = this.getHeaders();

        return { ...config, headers: { ...headers, ...config.headers } };
    }

    protected getBaseUrl() {
        if (window.location.href.startsWith('https://www.honeybook.com')) {
            return 'https://api.honeybook.com';
        }
        return process.env.REACT_APP_BASE_URL;
    }

    getHeaders(): Record<string, string> {
        return {};
    }

    protected setupInterceptors() {
        // Request interceptor
        axios.interceptors.request.use(async initialConfig => {
            try {
                // Run all request middlewares in sequence
                let updatedConfig = initialConfig;
                for (const middleware of this.requestMiddlewares) {
                    updatedConfig = await middleware(updatedConfig);
                }
                return updatedConfig;
            } catch (error) {
                return Promise.reject(error);
            }
        });

        // Response interceptor
        axios.interceptors.response.use(async initialResponse => {
            try {
                // Run all response middlewares in sequence
                let updatedResponse = initialResponse;
                for (const middleware of this.responseMiddlewares) {
                    updatedResponse = await middleware(updatedResponse);
                }

                return updatedResponse;
            } catch (error) {
                return Promise.reject(error);
            }
        });
    }

    addRequestMiddleware(
        middleware: (
            config: AxiosRequestConfig
        ) => Promise<AxiosRequestConfig> | AxiosRequestConfig
    ) {
        this.requestMiddlewares.push(middleware);
    }

    addResponseMiddleware(
        middleware: (
            response: AxiosResponse
        ) => Promise<AxiosResponse> | AxiosResponse
    ) {
        this.responseMiddlewares.push(middleware);
    }
}
